/**
 * @fileoverview MarketLabel — renders the display label for a market mapping record.
 *
 * Extracts the shared market-label rendering logic used in both
 * {@link MasterGroupedView} and {@link ListGroupedView}.
 *
 * Rules (matching the backend conventions):
 *  - If `marketType` is the secondary-market sentinel (`"sm"`), show the
 *    human-readable name from {@link MARKET_TYPE_NAMES} keyed by `marketId`.
 *  - Otherwise show `marketType` directly (e.g. `"axs"`, `"etix"`).
 */

import * as React from 'react';
import { getMarketName, SM_MARKET_TYPE } from '../utils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link MarketLabel}. */
export interface MarketLabelProps {
    /** Numeric market identifier (0 = Primary, 1 = Vivid, 2 = TN, 13 = GC). */
    marketId: number;
    /** Market type string — `"sm"` for primary/secondary-market records, otherwise the raw type. */
    marketType: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the human-readable market label for a mapping record.
 *
 * When `marketType` is `"sm"`, the label is looked up from {@link MARKET_TYPE_NAMES}
 * by `marketId` (e.g. `0 → "Primary"`, `1 → "VV"`).
 * For any other `marketType` value the raw type string is rendered as-is.
 *
 * @param props.marketId   - Numeric market identifier.
 * @param props.marketType - Raw market type string from the backend.
 *
 * @example
 * // Renders "Primary"
 * <MarketLabel marketId={0} marketType="sm" />
 *
 * @example
 * // Renders "axs"
 * <MarketLabel marketId={99} marketType="axs" />
 */
const MarketLabel: React.FC<MarketLabelProps> = ({ marketId, marketType }) => {
    const label = React.useMemo(() => {
        if (marketType === SM_MARKET_TYPE) {
            return getMarketName(marketId);
        }
        return marketType;
    }, [marketId, marketType]);

    return <>{label}</>;
};

export default MarketLabel;
